import { AbstractControl, ValidationErrors, ValidatorFn, FormGroup } from '@angular/forms';

export class RateValidators {

  /**
   * Validator for kmInterval - required if kmFee is set, valid if both are empty
   */
  static kmIntervalRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const kmFeeControl = formGroup.get('kmFee');
      const kmFee = kmFeeControl ? kmFeeControl.value : null;
      const kmInterval = control.value;

      // If kmFee is set, kmInterval must be set
      if (kmFee != null && kmFee !== '' && (kmInterval == null || kmInterval === '')) {
        return { kmIntervalRequired: true };
      }

      // If both are empty, it's valid
      if ((kmFee == null || kmFee === '') && (kmInterval == null || kmInterval === '')) {
        return null;
      }

      return null;
    };
  }

  /**
   * Validator for kmFee - required if kmInterval is set, valid if both are empty
   */
  static kmFeeRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const kmIntervalControl = formGroup.get('kmInterval');
      const kmInterval = kmIntervalControl ? kmIntervalControl.value : null;
      const kmFee = control.value;

      // If kmInterval is set, kmFee must be set
      if (kmInterval != null && kmInterval !== '' && (kmFee == null || kmFee === '')) {
        return { kmFeeRequired: true };
      }

      // If both are empty, it's valid
      if ((kmFee == null || kmFee === '') && (kmInterval == null || kmInterval === '')) {
        return null;
      }

      return null;
    };
  }

  /**
   * Validator for kmFeeOverThreshold - required if kmIntervalThreshold is set
   */
  static kmFeeOverThresholdRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const kmIntervalThresholdControl = formGroup.get('kmIntervalThreshold');
      const kmIntervalThreshold = kmIntervalThresholdControl ? kmIntervalThresholdControl.value : null;
      const kmFeeOverThreshold = control.value;

      // If kmIntervalThreshold is set, kmFeeOverThreshold must be set
      if (kmIntervalThreshold != null && kmIntervalThreshold !== '' && (kmFeeOverThreshold == null || kmFeeOverThreshold === '')) {
        return { kmFeeOverThresholdRequired: true };
      }

      return null;
    };
  }

  /**
   * Validator for activeFeeOverThreshold - required if intervalThreshold is set
   */
  static activeFeeOverThresholdRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const intervalThresholdControl = formGroup.get('activeIntervalThreshold');
      const intervalThreshold = intervalThresholdControl ? intervalThresholdControl.value : null;
      const activeFeeOverThreshold = control.value;

      if (intervalThreshold != null && intervalThreshold !== '' && (activeFeeOverThreshold == null || activeFeeOverThreshold === '')) {
        return { activeFeeOverThresholdRequired: true };
      }

      return null;
    };
  }

  /**
   * Validator for breakFeeOverThreshold - required if intervalThreshold is set
   */
  static breakFeeOverThresholdRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const intervalThresholdControl = formGroup.get('breakIntervalThreshold');
      const intervalThreshold = intervalThresholdControl ? intervalThresholdControl.value : null;
      const breakFeeOverThreshold = control.value;

      if (intervalThreshold != null && intervalThreshold !== '' && (breakFeeOverThreshold == null || breakFeeOverThreshold === '')) {
        return { breakFeeOverThresholdRequired: true };
      }

      return null;
    };
  }

  /**
   * Validator for bookingFeeOverThreshold - required if intervalThreshold is set
   */
  static bookingFeeOverThresholdRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const intervalThresholdControl = formGroup.get('bookingIntervalThreshold');
      const intervalThreshold = intervalThresholdControl ? intervalThresholdControl.value : null;
      const bookingFeeOverThreshold = control.value;

      if (intervalThreshold != null && intervalThreshold !== '' && (bookingFeeOverThreshold == null || bookingFeeOverThreshold === '')) {
        return { bookingFeeOverThresholdRequired: true };
      }

      return null;
    };
  }

  /**
   * Validator to ensure kmIntervalThreshold is only set when both kmFee and kmInterval are present
   */
  static kmIntervalThresholdLogic(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const kmFeeControl = formGroup.get('kmFee');
      const kmIntervalControl = formGroup.get('kmInterval');
      const kmFee = kmFeeControl ? kmFeeControl.value : null;
      const kmInterval = kmIntervalControl ? kmIntervalControl.value : null;
      const kmIntervalThreshold = control.value;

      // If kmIntervalThreshold is set, must have both kmFee and kmInterval
      if (kmIntervalThreshold != null && kmIntervalThreshold !== '') {
        if ((kmFee == null || kmFee === '') || (kmInterval == null || kmInterval === '')) {
          return { kmIntervalThresholdWithoutBase: true };
        }
      }

      return null;
    };
  }

  /**
   * Validator to ensure fee over threshold fields are only set when intervalThreshold is present
   */
  static feeOverThresholdLogic(fieldName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const intervalThresholdControl = formGroup.get(fieldName);
      const intervalThreshold = intervalThresholdControl ? intervalThresholdControl.value : null;
      const feeOverThreshold = control.value;

      if (feeOverThreshold != null && feeOverThreshold !== '') {
        if (intervalThreshold == null || intervalThreshold === '') {
          return { feeOverThresholdWithoutBase: true };
        }
      }

      return null;
    };
  }

  /**
   * Validator to ensure kmFeeOverThreshold is only set when kmIntervalThreshold is present
   */
  static kmFeeOverThresholdLogic(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control.parent instanceof FormGroup)) {
        return null;
      }

      const formGroup = control.parent;
      const kmIntervalThresholdControl = formGroup.get('kmIntervalThreshold');
      const kmIntervalThreshold = kmIntervalThresholdControl ? kmIntervalThresholdControl.value : null;
      const kmFeeOverThreshold = control.value;

      if (kmFeeOverThreshold != null && kmFeeOverThreshold !== '') {
        if (kmIntervalThreshold == null || kmIntervalThreshold === '') {
          return { kmFeeOverThresholdWithoutBase: true };
        }
      }

      return null;
    };
  }
}